import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../../components/ui/Icon';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Skeleton } from '../../../components/ui/Skeleton';
import { MEmpty, MGroup, MPage, MRow } from '../../../components/mobile/MobileKit';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { AdminHeader, useSyncAll } from '../shared/AdminShell';
import { SectionMark } from '../../../components/ui/SectionMark';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { useModalStore } from '../../../stores/modal.store';
import { useAuthStore } from '../../../stores/auth.store';
import { canWriteAdmin } from '../adminApi';
import { LineaSaldo } from './LineaSaldo';
import '../fatture/fatture.css';
import { AzioniTassa, NuovaTassa } from './AzioniTassa';
import { AltrePagine, AvvisoCollegamenti, CoseDaFare, DaDoveVengono } from './CalendarioExtra';
import { Foglio } from '../shared/AdminMobile';
import { Fascia } from '../fisco/parts';
import type { VoceFascia } from '../fisco/fiscoApi';
import {
  calendarioQuery, conArticolo, dataCorta, quandoBreve, tonoSaldo,
  type CalendarioPagamenti, type GiornoCalendario, type MeseCalendario, type VoceCalendario,
} from './debitiApi';
import '../admin.css';
import './debiti.css';
import './calendario.css';

/**
 * Calendario dei pagamenti (`/admin/calendario`): la metà "Calendar" dell'idea
 * di Lorenzo del 16/10, accanto alla bacheca dei debiti.
 *
 * I mesi scorrono uno sotto l'altro, senza cambio pagina (Lorenzo, 14/7: per i
 * calendari "più uno scroll continuo", stile Google/Notion), con l'intestazione
 * del mese che resta in alto mentre si scorre. Ogni giorno ha i suoi pagamenti
 * e, da oggi in poi, il saldo previsto a fine giornata. Nel saldo solo le uscite
 * certe: IVA, cartelle e avvisi sono "da decidere" e si vedono a parte.
 *
 * Telefono: niente griglia, una lista per giorno (regola mobile e desktop separati).
 */
export function CalendarioPage() {
  const isMobile = useIsMobile();
  const [mesi, setMesi] = useState(6);
  const q = useQuery(calendarioQuery(new Date(), mesi));
  const [aperto, setAperto] = useUrlState<string>('giorno', '');
  const d = q.data;
  const perData = useMemo(() => new Map((d?.giorni ?? []).map((g) => [g.data, g])), [d]);
  const giorno = aperto ? perData.get(aperto) ?? { data: aperto, voci: [], saldo: null } : null;
  const altri = () => setMesi((m) => Math.min(m + 6, 12));

  if (!d) {
    return (
      <div className="page adm-page cal-page">
        <AdminHeader title="Calendario dei pagamenti" crumb={false} />
        {q.isLoading
          ? <Skeleton size="block" h={420} />
          : <EmptyState icon="calendar" title="Calendario non disponibile" description="Non riesco a leggere i pagamenti in questo momento. Riprova tra poco." />}
      </div>
    );
  }
  return isMobile
    ? <CalendarioMobile d={d} giorno={giorno} onApri={setAperto} onAltri={mesi < 12 ? altri : undefined} />
    : <CalendarioDesktop d={d} perData={perData} giorno={giorno} onApri={setAperto} onAltri={mesi < 12 ? altri : undefined} caricando={q.isFetching} />;
}

interface Props {
  d: CalendarioPagamenti;
  giorno: GiornoCalendario | null;
  onApri: (data: string) => void;
  onAltri?: () => void;
}

const MAIUSC = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const GIORNI_SETT = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];
const GIORNI_LUNGHI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MESI_LUNGHI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

/** "Lunedì 16 novembre" (con l'anno se non è quello di oggi). */
function giornoInParole(iso: string, oggi: string): string {
  const [y, m, g] = iso.split('-').map(Number);
  const dow = new Date(Date.UTC(y!, m! - 1, g!)).getUTCDay();
  return `${MAIUSC(GIORNI_LUNGHI[dow]!)} ${g} ${MESI_LUNGHI[m! - 1]}${String(y) === oggi.slice(0, 4) ? '' : ` ${y}`}`;
}

const CONTO = 'Contando solo le uscite certe e le entrate già fatturate o a canone.';

/**
 * La testata. Dal 26/10/2026 il saldo si legge col fido Qonto: sotto zero il
 * conto regge (si usa il fido, fino a -fido), il limite vero è oltre il fido.
 * Quindi la data che conta è il primo giorno OLTRE il fido; il primo giorno
 * sotto zero diventa la riga sotto ("da lì si usa il fido").
 */
function fascia(d: CalendarioPagamenti): VoceFascia[] {
  const daDecidere = d.mesi.reduce((s, m) => s + m.esceDaDecidere, 0);
  const fido = d.fido ?? 0;
  const buco: VoceFascia = d.oltreFido
    ? {
        key: 'buco', label: 'Primo giorno oltre il fido Qonto', valore: null, testo: dataCorta(d.oltreFido.data, d.oggi),
        sotto: `saldo previsto € ${fmtEur(d.oltreFido.saldo, true)}`,
        aiuto: `Il fido Qonto fa scendere il conto fino a € -${fmtEur(fido)}: oltre, gli addebiti non passano. ${CONTO}`,
        tono: 'alert', fonte: null,
      }
    : d.primoBuco
      ? {
          key: 'buco', label: 'Primo giorno con il conto sotto zero', valore: null, testo: dataCorta(d.primoBuco.data, d.oggi),
          sotto: fido > 0 ? `si usa il fido Qonto, fino a € -${fmtEur(fido)}` : `saldo previsto € ${fmtEur(d.primoBuco.saldo, true)}`,
          aiuto: CONTO, tono: fido > 0 ? null : 'alert', fonte: null,
        }
      : { key: 'buco', label: 'Conto sotto zero nei prossimi mesi', valore: null, testo: 'Mai', sotto: 'con le uscite certe', aiuto: null, tono: null, fonte: null };
  return [
    { key: 'oggi', label: 'Soldi sul conto oggi', valore: d.saldoOggi, testo: null, sotto: 'Qonto, tutti i conti', aiuto: null, tono: null, fonte: null },
    buco,
    { key: 'decidere', label: 'Tasse da decidere, fuori dal saldo', valore: daDecidere, testo: null, sotto: 'IVA, cartelle e avvisi: li decide Andrea', aiuto: null, tono: null, fonte: null },
  ];
}

/** Sotto la data oltre il fido, quando si comincia a usarlo: "Sotto zero da oggi: si usa il fido, fino a € -5.000". */
function rigaFido(d: CalendarioPagamenti): ReactNode {
  if (!d.oltreFido || !d.primoBuco || !(d.fido > 0)) return null;
  const da = quandoBreve(d.primoBuco.data, d.oggi);
  return <div className="st-delta cal-fido">Sotto zero {da === 'oggi' || da === 'domani' ? `da ${da}` : `dal ${da}`}: si usa il fido, fino a € -{fmtEur(d.fido)}</div>;
}

// ════════════════════════════════════════════════════════════════════
// Computer
// ════════════════════════════════════════════════════════════════════

/**
 * Rifinito il 26/10/2026 (CAPITOLATO: "il calendario c'è già: in testa la linea del saldo col fido").
 * La domanda: quando escono ed entrano i soldi, e quando non bastano?
 * In cima la risposta in una frase (fino a quando bastano col fido, e che le tasse da decidere
 * sono fuori dal conto), poi la linea del saldo col fido (LineaSaldo.tsx), le cose da fare e i mesi.
 * Tolti dalla vista (il codice resta): la fascia dei tre numeri, il sottotitolo di tre righe, il
 * bottone "Debiti col fisco" (sta nel menu a sinistra), "Da dove vengono i numeri" (l'ora della banca
 * è nella frase). "Nuova tassa da pagare" passa nel menu ⋯. Il fido resta quello di admin.calendario.ts.
 */
function CalendarioDesktop({ d, perData, giorno, onApri, onAltri, caricando }: Props & { perData: Map<string, GiornoCalendario>; caricando: boolean }) {
  const sync = useSyncAll();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const menu: ActionItem[] = [
    { key: 'tassa', label: 'Nuova tassa da pagare', icon: 'plus', onClick: () => openModal('newTax'), hidden: !canWriteAdmin(user) },
  ];
  return (
    <div className="page adm-page ft-page cal-page">
      <header className="ft-hd">
        <div className="ft-hd-main">
          <SectionMark icon="calendar" className="ft-sez" />
          <div className="min-w-0">
            <h1 className="ft-title">Calendario dei pagamenti</h1>
            <p className="ft-risposta"><Risposta d={d} /></p>
          </div>
        </div>
        <div className="ft-hd-azioni">
          {sync.button}
          <ActionsMenu variant="ghost" label="Altre azioni" items={menu} />
        </div>
      </header>
      <AvvisoCollegamenti />
      <LineaSaldo d={d} onApri={onApri} />
      {MOSTRA_FASCIA && <Fascia voci={fascia(d)} extra={{ buco: rigaFido(d) }} />}
      <CoseDaFare />

      {d.mesi.map((m) => <Mese key={m.mese} m={m} oggi={d.oggi} fido={d.fido ?? 0} perData={perData} onApri={onApri} />)}

      {onAltri && (
        <div className="cal-altri">
          <button className="btn" onClick={onAltri} disabled={caricando}>
            <Icon name="calendarPlus" size={12} /> {caricando ? 'Carico i mesi…' : 'Mostra altri sei mesi'}
          </button>
        </div>
      )}

      {MOSTRA_FASCIA && <DaDoveVengono />}

      <PannelloGiorno g={giorno} oggi={d.oggi} fido={d.fido ?? 0} onClose={() => onApri('')} />
    </div>
  );
}

/** La fascia dei tre numeri e "Da dove vengono i numeri": nascoste il 26/10/2026, si rimettono da qui. */
const MOSTRA_FASCIA = false;

/**
 * La risposta: quanto c'è oggi, fino a quando bastano i soldi col fido Qonto, e che le tasse
 * da decidere stanno fuori dal conto (prima non si capiva). Ogni numero una volta.
 */
function Risposta({ d }: { d: CalendarioPagamenti }) {
  const fido = d.fido ?? 0;
  const daDecidere = d.mesi.reduce((s, m) => s + m.esceDaDecidere, 0);
  const prima = (iso: string) => new Date(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)) - 1)).toISOString().slice(0, 10);
  return (
    <>
      Oggi sul conto ci sono <strong>€ {fmtEur(d.saldoOggi, true)}</strong>.{' '}
      {d.oltreFido ? (
        <>Col fido di Qonto i soldi bastano fino {conArticolo(prima(d.oltreFido.data), d.oggi, 'al')}: <span className="is-alert">{conArticolo(d.oltreFido.data, d.oggi)} il saldo previsto va oltre i € {fmtEur(fido)} del fido</span>.</>
      ) : d.primoBuco ? (
        <>{d.primoBuco.data === d.oggi ? 'Siamo già sotto zero' : <>Si scende sotto zero {conArticolo(d.primoBuco.data, d.oggi)}</>}, ma il fido di Qonto basta per tutti i mesi qui sotto.</>
      ) : (
        <>I soldi bastano per tutti i mesi qui sotto, senza usare il fido.</>
      )}
      {daDecidere > 0 && <> Le tasse da decidere (IVA, cartelle e avvisi, € {fmtEur(daDecidere)}) sono fuori da questi conti: le decide Andrea.</>}
    </>
  );
}

function Mese({ m, oggi, fido, perData, onApri }: { m: MeseCalendario; oggi: string; fido: number; perData: Map<string, GiornoCalendario>; onApri: (d: string) => void }) {
  const [y, mm] = m.mese.split('-').map(Number);
  const giorniMese = new Date(Date.UTC(y!, mm!, 0)).getUTCDate();
  const primo = (new Date(Date.UTC(y!, mm! - 1, 1)).getUTCDay() + 6) % 7; // lunedì = 0
  const celle: Array<string | null> = [
    ...Array.from({ length: primo }, () => null),
    ...Array.from({ length: giorniMese }, (_, i) => `${m.mese}-${String(i + 1).padStart(2, '0')}`),
  ];
  while (celle.length % 7) celle.push(null);

  return (
    <section className="cal-mese" aria-label={MAIUSC(m.nome)}>
      <header className="cal-mese-hd">
        <h2 className="cal-mese-t">{MAIUSC(m.nome)}</h2>
        <div className="cal-mese-somme">
          {/* Nel mese in corso si conta da oggi: lo dice l'etichetta (26/10/2026, prima non si capiva). */}
          <span>{m.mese === oggi.slice(0, 7) ? 'Escono da oggi' : 'Escono'} <b className="mono">€ {fmtEur(m.esceCerto)}</b></span>
          {m.esceDaDecidere > 0 && <span className="is-decidere">Tasse da decidere <b className="mono">€ {fmtEur(m.esceDaDecidere)}</b></span>}
          <span>Entrano <b className="mono">€ {fmtEur(m.entra)}</b></span>
          {m.saldoFine != null && (
            <span className={tonoSaldo(m.saldoFine, fido)}>Saldo previsto a fine mese <b className="mono">€ {fmtEur(m.saldoFine)}</b></span>
          )}
        </div>
        <div className="cal-sett" aria-hidden>
          {GIORNI_SETT.map((g) => <div key={g}>{g}</div>)}
        </div>
      </header>
      <div className="cal-griglia">
        {celle.map((c, i) => (c ? <Giorno key={c} data={c} g={perData.get(c)} oggi={oggi} fido={fido} onApri={onApri} /> : <div key={`v${i}`} className="cal-g is-vuoto" />))}
      </div>
    </section>
  );
}

const VISIBILI = 3;

function Giorno({ data, g, oggi, fido, onApri }: { data: string; g: GiornoCalendario | undefined; oggi: string; fido: number; onApri: (d: string) => void }) {
  const voci = g?.voci ?? [];
  const passato = data < oggi;
  const tono = tonoSaldo(g?.saldo, fido);
  const n = Number(data.slice(8));
  return (
    <button
      type="button"
      className={`cal-g${data === oggi ? ' is-oggi' : ''}${passato ? ' is-passato' : ''}${voci.length ? '' : ' is-libero'}`}
      onClick={() => voci.length && onApri(data)}
      disabled={!voci.length}
      aria-label={`${n}: ${voci.length} pagamenti`}
    >
      <div className="cal-g-hd">
        <span className="cal-g-n">{n}</span>
        {g?.saldo != null && voci.length > 0 && (
          <span className={`cal-g-saldo mono${tono ? ` ${tono}` : ''}`} title={tono === 'is-fido' ? 'Saldo previsto a fine giornata: sotto zero, coperto dal fido Qonto' : tono === 'is-neg' ? 'Saldo previsto a fine giornata: oltre il fido Qonto' : 'Saldo previsto a fine giornata'}>
            € {fmtEur(g.saldo)}
          </span>
        )}
      </div>
      {voci.slice(0, VISIBILI).map((v) => <Chip key={v.key} v={v} />)}
      {voci.length > VISIBILI && <div className="cal-piu">e altri {voci.length - VISIBILI}</div>}
    </button>
  );
}

function Chip({ v }: { v: VoceCalendario }) {
  return (
    <div className={`cal-v is-${v.stato}${v.daDecidere ? ' is-decidere' : ''}${v.verso === 'entra' ? ' is-entra' : ''}`} title={`${v.titolo} · € ${fmtEur(v.importo, true)}${v.avviso ? `\n${v.avviso}` : ''}`}>
      <span className="cal-v-ico" aria-hidden>{v.stato === 'pagato' ? <Icon name="check" size={8} /> : null}</span>
      <span className="cal-v-t">{v.titolo}</span>
      <span className="cal-v-e mono">{v.verso === 'entra' ? '+' : ''}{fmtEur(v.importo)}</span>
    </div>
  );
}

/** Il giorno aperto, a destra: tutte le voci divise per tipo. In un portale su <body>, come la scheda dei debiti. */
function PannelloGiorno(props: { g: GiornoCalendario | null; oggi: string; fido: number; onClose: () => void }) {
  return createPortal(<PannelloGiornoDentro {...props} />, document.body);
}

function PannelloGiornoDentro({ g, oggi, fido, onClose }: { g: GiornoCalendario | null; oggi: string; fido: number; onClose: () => void }) {
  const [vista, setVista] = useState(g);
  useEffect(() => { if (g) setVista(g); }, [g]);
  useEffect(() => {
    if (!g) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [g, onClose]);
  const open = !!g;
  return (
    <>
      <div className={`deb-velo${open ? ' is-open' : ''}`} onClick={onClose} aria-hidden />
      <aside className={`deb-panel${open ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-label="Pagamenti del giorno">
        {vista && (
          <>
            <div className="deb-panel-hd">
              <div className="min-w-0">
                <div className="deb-doc">{vista.data < oggi ? 'Giorno passato' : vista.data === oggi ? 'Oggi' : 'Pagamenti previsti'}</div>
                <h2 className="deb-panel-title">{giornoInParole(vista.data, oggi)}</h2>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={onClose} title="Chiudi (Esc)"><Icon name="x" size={14} /></button>
            </div>
            <div className="deb-panel-body">
              <DettaglioGiorno g={vista} oggi={oggi} fido={fido} />
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function DettaglioGiorno({ g, oggi, fido }: { g: GiornoCalendario; oggi: string; fido: number }) {
  const gruppi: Array<{ k: string; t: string; voci: VoceCalendario[] }> = [
    { k: 'certe', t: 'Uscite da pagare', voci: g.voci.filter((v) => v.verso === 'esce' && !v.daDecidere && v.stato !== 'pagato') },
    { k: 'decidere', t: 'Tasse da decidere, fuori dal saldo', voci: g.voci.filter((v) => v.verso === 'esce' && v.daDecidere && v.stato !== 'pagato') },
    { k: 'entra', t: 'Entrate attese', voci: g.voci.filter((v) => v.verso === 'entra') },
    { k: 'pagate', t: 'Già pagate', voci: g.voci.filter((v) => v.stato === 'pagato') },
  ];
  const tono = tonoSaldo(g.saldo, fido);
  return (
    <>
      {g.saldo != null && (
        <dl className="deb-fatti">
          <dt>Saldo previsto a fine giornata</dt>
          <dd className={`mono${tono === 'is-neg' ? ' cal-neg' : ''}`}>€ {fmtEur(g.saldo, true)}</dd>
          {tono && fido > 0 && (
            <dd className={`cal-fatti-nota${tono === 'is-neg' ? ' cal-neg' : ''}`}>
              {tono === 'is-neg' ? `oltre il fido Qonto, che arriva fino a € -${fmtEur(fido)}` : `sotto zero: si usa il fido Qonto, fino a € -${fmtEur(fido)}`}
            </dd>
          )}
        </dl>
      )}
      {gruppi.filter((x) => x.voci.length).map((x) => (
        <section key={x.k} className="deb-sez">
          <h3 className="deb-sez-t">{x.t} <span className="deb-sez-n">{x.voci.length}</span></h3>
          {x.voci.map((v) => (
            <div key={v.key} className="cal-riga">
              <div className="min-w-0 flex-1">
                <div className="cal-riga-t">{v.titolo}{v.stima && <span className="cal-tag">stima</span>}</div>
                <div className="cal-riga-s">
                  {v.stato === 'pagato' ? `Pagato${v.pagataIl && v.pagataIl !== g.data ? ` il ${dataCorta(v.pagataIl, oggi)}` : ''}` : v.stato === 'scaduto' ? 'Scaduto, non pagato' : v.come}
                  {v.daDecidere && ' · decide Andrea'}
                </div>
                {v.avviso && v.stato !== 'pagato' && <div className="cal-avviso"><Icon name="alert" size={11} /> {v.avviso}</div>}
                {v.debitoId && <Link className="cal-link" to={`/admin/debiti?carta=${v.debitoId}`}>Apri nella bacheca dei debiti</Link>}
                {v.tassa && (
                  <AzioniTassa
                    tassa={v.tassa}
                    rata={v.installmentId && v.stato !== 'pagato' ? { id: v.installmentId, importo: v.importo } : null}
                    nome={v.titolo}
                    aperta={v.stato !== 'pagato'}
                    compatta
                  />
                )}
              </div>
              <div className={`mono cal-riga-e${v.stato === 'scaduto' ? ' cal-neg' : ''}`}>{v.verso === 'entra' ? '+ ' : ''}€ {fmtEur(v.importo, true)}</div>
            </div>
          ))}
        </section>
      ))}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Telefono: una lista per giorno, i mesi uno sotto l'altro
// ════════════════════════════════════════════════════════════════════

function CalendarioMobile({ d, giorno, onApri, onAltri }: Props) {
  const sync = useSyncAll();
  const daOggi = d.giorni.filter((g) => g.data >= d.oggi && g.voci.length);
  const perMese = new Map<string, GiornoCalendario[]>();
  for (const g of daOggi) perMese.set(g.data.slice(0, 7), [...(perMese.get(g.data.slice(0, 7)) ?? []), g]);
  const buco = d.primoBuco;
  const oltre = d.oltreFido;
  const fido = d.fido ?? 0;

  // 26/10/2026: dentro `.page` come le altre pagine del telefono (prima il contenuto toccava il
  // bordo dello schermo) e con l'intestazione per prima (mobile.md 4bis: niente titolo doppio).
  return (
    <div className="page adm-page">
    <AdminHeader title="Calendario dei pagamenti" menu={[sync.menuItem]} />
    <MPage>
      <AvvisoCollegamenti />
      <div className="cal-m-top">
        <div><span className="cal-m-k">Sul conto oggi</span><b className="mono">€ {fmtEur(d.saldoOggi, true)}</b></div>
        {/* Col fido Qonto la data che conta è quella oltre il fido; sotto zero il conto regge. */}
        <div className={oltre ? 'is-neg' : ''}>
          <span className="cal-m-k">{oltre ? 'Oltre il fido Qonto dal' : 'Sotto zero dal'}</span>
          <b>{oltre ? dataCorta(oltre.data, d.oggi) : buco ? dataCorta(buco.data, d.oggi) : 'mai'}</b>
        </div>
      </div>
      {buco && fido > 0 && (
        <div className="cal-m-fido">
          {oltre
            ? `Sotto zero ${quandoBreve(buco.data, d.oggi) === 'oggi' ? 'da oggi' : `dal ${dataCorta(buco.data, d.oggi)}`}: si usa il fido, fino a € -${fmtEur(fido)}.`
            : `Sotto zero si usa il fido Qonto, fino a € -${fmtEur(fido)}.`}
        </div>
      )}
      {d.mesi.filter((m) => perMese.has(m.mese)).map((m) => (
        <div key={m.mese} className="cal-m-mese">
          <div className="cal-m-mese-hd">
            <span>{MAIUSC(m.nome)}</span>
            {m.saldoFine != null && <span className={`mono${tonoSaldo(m.saldoFine, fido) === 'is-neg' ? ' is-neg' : ''}`}>fine mese € {fmtEur(m.saldoFine)}</span>}
          </div>
          {perMese.get(m.mese)!.map((g) => (
            <MGroup key={g.data} title={giornoInParole(g.data, d.oggi)} note={g.saldo != null ? `Saldo previsto a fine giornata € ${fmtEur(g.saldo, true)}${tonoSaldo(g.saldo, fido) === 'is-neg' ? ', oltre il fido' : ''}` : undefined}>
              {g.voci.map((v) => (
                <MRow
                  key={v.key}
                  title={v.titolo}
                  sub={v.stato === 'pagato' ? 'Pagato' : !v.come && !v.daDecidere && !v.avviso ? undefined : (
                    <>
                      {v.daDecidere ? `${v.come ?? ''} · decide Andrea` : v.come}
                      {v.avviso && <span className="cal-avviso">{v.avviso}</span>}
                    </>
                  )}
                  subWrap
                  trail={`${v.verso === 'entra' ? '+ ' : ''}€ ${fmtEur(v.importo, true)}`}
                  trailTone={v.stato === 'scaduto' ? 'alert' : v.stato === 'pagato' || v.daDecidere ? 'mute' : undefined}
                  onClick={() => onApri(g.data)}
                />
              ))}
            </MGroup>
          ))}
        </div>
      ))}
      {daOggi.length === 0 && <MEmpty>Nessun pagamento nei prossimi mesi.</MEmpty>}
      {onAltri && <button className="btn w-full mt-4" onClick={onAltri}>Mostra altri sei mesi</button>}
      <CoseDaFare compatta />
      <AltrePagine qui="calendario" />

      <Foglio open={!!giorno} onClose={() => onApri('')} title={giorno ? giornoInParole(giorno.data, d.oggi) : undefined}>
        {giorno && <div className="cal-m-foglio"><DettaglioGiorno g={giorno} oggi={d.oggi} fido={fido} /></div>}
      </Foglio>
    </MPage>
    </div>
  );
}
